import type { ReactElement } from 'react'
import type { Route } from '@metro/routing'
import { abbreviate } from './abbreviate'
import { LineBadge } from './LineBadge'
import styles from './RouteVariantRow.module.css'

/**
 * Display info for a metro line, resolved from schema by the caller.
 *
 * Kept independent of the schema shape so {@link RouteVariantRow} stays
 * schema-agnostic — only line_id → display lookup is needed.
 */
export interface LineDisplay {
  /** Localised line name, e.g. "Central line". First 3 chars uppercased → badge when there is no {@link shortName}. */
  readonly localName: string
  /** Hex color, e.g. "#E32017". Used as the badge background. */
  readonly hexColor: string
  /**
   * The line's own badge label from the schema (`shortLocalName`, e.g. "TRAM",
   * "FERRY", "ELIZ"). Wins over the 3-letter cut of {@link localName}, which
   * turned "Trams" into "TRA" (TASK-986, same rule on iOS / Android).
   */
  readonly shortName?: string
}

/**
 * Props for {@link RouteVariantRow}.
 */
export interface RouteVariantRowProps {
  /** The route variant to render. */
  readonly route: Route
  /** Selected variants render with a textPrimary stroke; unselected with a 50%-opacity textSecondary stroke. */
  readonly isSelected: boolean
  /** Maps line_id → display info. Caller resolves from schema; component is schema-agnostic. */
  readonly lineLookup: ReadonlyMap<string, LineDisplay>
  /** Tap handler — fires when the user clicks/taps the row. */
  readonly onTap: () => void
  /** Test seam for the "now" clock used to compute arrival HH:mm. Defaults to `() => new Date()`. */
  readonly clock?: () => Date
  /**
   * Layout variant. `phone` (default): compact card with minutes/arrival
   * stacked over a "<N> transfers" subtitle and badges row.
   * `tablet`: wider card with minutes/arrival on the left, transfers
   * label on the RIGHT of the same row, badges below — Figma TASK-275
   * §4.2.
   */
  readonly variant?: 'phone' | 'tablet'
  /**
   * 1-based index of this variant in the surrounding list. When provided
   * together with {@link totalVariants}, the screen-reader label
   * gains an "Option N of M" prefix so the user knows which slot they
   * are on in the carousel / column.
   */
  readonly variantIndex?: number
  /** Total number of variants in the surrounding list. See {@link variantIndex}. */
  readonly totalVariants?: number
}

const FALLBACK_BADGE_COLOR = '#888888'

// Arrival clock of the city the app is built for: `VITE_TIME_ZONE`, which every
// build sets — `.env` for the default (London) build, `.env.<city>` for another
// city (e.g. America/New_York). No fallback: a build without it would be on the
// browser's zone, not the city's.
const ARRIVAL_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: import.meta.env.VITE_TIME_ZONE,
  hour12: false,
})

/**
 * Single variant cell for the Routing results state.
 *
 * Renders, top to bottom:
 * - `<N> min` (left) + `~HH:mm` arrival in the city's time zone, 24h (right)
 * - `direct` if `transfer_count === 0`, else `1 transfer` / `<N> transfers`
 *   (singular for exactly one, matching the Figma chip copy)
 * - One color-coded badge per unique line_id seen in the route's
 *   ride legs (deduped, in route order). Walk / transfer legs do not
 *   contribute badges.
 *
 * The pill is a real `<button type="button">` so keyboard activation
 * (Enter / Space) works out of the box; selected state is exposed via
 * `aria-pressed` and a `data-selected` attribute for tests.
 *
 * Schema-agnostic: caller passes `lineLookup` (line_id → {localName,
 * hexColor}). Missing entries fall back to a neutral grey badge with
 * the line_id's first 3 chars upper-cased as the abbreviation.
 *
 * Clock injection: arrival time is `clock() + total_time_s` formatted
 * via `Intl.DateTimeFormat` in the city's time zone (`VITE_TIME_ZONE`). Default `clock` is
 * `() => new Date()`; tests pass a fixed clock for determinism.
 */
export function RouteVariantRow({
  route,
  isSelected,
  lineLookup,
  onTap,
  clock = () => new Date(),
  variant = 'phone',
  variantIndex,
  totalVariants,
}: RouteVariantRowProps): ReactElement {
  const minutes = Math.round(route.total_time_s / 60)
  const arrivalDate = new Date(clock().getTime() + route.total_time_s * 1000)
  const arrival = ARRIVAL_FORMATTER.format(arrivalDate)

  const subtitle =
    route.transfer_count === 0
      ? 'direct'
      : `${route.transfer_count} ${route.transfer_count === 1 ? 'transfer' : 'transfers'}`

  const lineIds = uniqueRideLegLineIds(route)

  // Build a screen-reader-friendly summary. `aria-label` is the only
  // surface a VoiceOver / NVDA / TalkBack user gets — visual treatments
  // (badges, .selected outline) are invisible to them. Compose: option
  // index ("Option 2 of 3") when known, minutes, transfer count, line
  // list ("via Central and Jubilee"), arrival HH:mm, and the selection
  // state. Audit ref: H-7.
  const lineSummary = humanLineSummary(lineIds, lineLookup)
  const positionPrefix =
    variantIndex !== undefined && totalVariants !== undefined && totalVariants > 0
      ? `Option ${variantIndex} of ${totalVariants}, `
      : ''
  const lineSuffix = lineSummary.length > 0 ? `, via ${lineSummary}` : ''
  const selectedSuffix = isSelected ? ', selected' : ''
  const ariaLabel =
    `${positionPrefix}` +
    `${minutes} minute route, ${subtitle}${lineSuffix}, ` +
    `arrives ${arrival}${selectedSuffix}`

  const buttonClasses = [
    variant === 'tablet' ? styles.buttonTablet : styles.button,
    isSelected ? styles.selected : '',
  ]
    .filter(Boolean)
    .join(' ')

  const badges = (
    <span className={styles.badges} data-testid="route-variant-row-badges">
      {lineIds.map((lineId) => {
        const display = lineLookup.get(lineId)
        const bg = display?.hexColor ?? FALLBACK_BADGE_COLOR
        const label = display?.shortName ?? abbreviate(display?.localName ?? lineId)
        return <LineBadge key={lineId} label={label} hexColor={bg} lineId={lineId} />
      })}
    </span>
  )

  if (variant === 'tablet') {
    // Figma TASK-275 §4.2: minutes/arrival on the left, transfers label
    // on the RIGHT of the same row; badges below.
    return (
      <button
        type="button"
        className={buttonClasses}
        aria-pressed={isSelected}
        aria-current={isSelected ? 'true' : undefined}
        aria-label={ariaLabel}
        data-selected={isSelected}
        data-variant="tablet"
        data-testid="route-variant-row"
        onClick={onTap}
      >
        <span className={styles.topRowTablet}>
          <span className={styles.titleGroupTablet}>
            <span className={styles.minutesTablet}>{minutes} min</span>
            <span className={styles.arrivalTablet}>~{arrival}</span>
          </span>
          <span className={styles.subtitleTablet}>{subtitle}</span>
        </span>
        {badges}
      </button>
    )
  }

  return (
    <button
      type="button"
      className={buttonClasses}
      aria-pressed={isSelected}
      aria-current={isSelected ? 'true' : undefined}
      aria-label={ariaLabel}
      data-selected={isSelected}
      data-variant="phone"
      data-testid="route-variant-row"
      onClick={onTap}
    >
      <span className={styles.topRow}>
        <span className={styles.minutes}>{minutes} min</span>
        <span className={styles.arrival}>~{arrival}</span>
      </span>
      <span className={styles.subtitle}>{subtitle}</span>
      {badges}
    </button>
  )
}

/**
 * Build a human-readable list of the lines this route uses, for the
 * screen-reader label. "Central", "Central and Jubilee",
 * "Central, Jubilee and Bakerloo". Missing lookup entries fall back
 * to the raw line_id so the announcement is still informative.
 */
function humanLineSummary(
  lineIds: readonly string[],
  lookup: ReadonlyMap<string, LineDisplay>,
): string {
  const names = lineIds.map((id) => lookup.get(id)?.localName ?? id)
  if (names.length === 0) return ''
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * Returns the line_ids of the route's ride legs in route order, with
 * duplicates removed. Walk and transfer legs are skipped. Ride legs
 * missing a `line_id` are skipped (defensive — the route schema treats
 * `line_id` as optional on the leg type).
 */
function uniqueRideLegLineIds(route: Route): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const leg of route.legs) {
    if (leg.type !== 'ride') continue
    const id = leg.line_id
    if (id === undefined || id === null) continue
    if (seen.has(id)) continue
    seen.add(id)
    out.push(id)
  }
  return out
}
